import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import SearchOverlay from './SearchOverlay';
import MiniCart from './MiniCart';
import { logos } from '../../assets/images';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [blogDropdownOpen, setBlogDropdownOpen] = useState(false);
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { cartItems, cartNotification } = useCart();
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const mobileProfileRef = useRef(null);
  const searchRef = useRef(null);
  const blogRef = useRef(null);
  const profileCloseTimeout = useRef(null);
  const blogCloseTimeout = useRef(null);
  const searchCloseTimeout = useRef(null);

  const handleLogout = () => {
    logout();
    setProfileDropdownOpen(false);
    navigate('/');
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleCloseMiniCart = () => {
    setMiniCartOpen(false);
  };

  const handleSearchMouseEnter = () => {
    if (searchCloseTimeout.current) {
      clearTimeout(searchCloseTimeout.current);
    }
    setSearchOpen(true);
    setProfileDropdownOpen(false);
    setBlogDropdownOpen(false);
  };

  const handleSearchMouseLeave = () => {
    searchCloseTimeout.current = setTimeout(() => {
      setSearchOpen(false);
    }, 300); // 300ms delay before closing
  };

  const handleProfileMouseEnter = () => {
    if (profileCloseTimeout.current) {
      clearTimeout(profileCloseTimeout.current);
    }
    setProfileDropdownOpen(true);
  };

  const handleProfileMouseLeave = () => {
    profileCloseTimeout.current = setTimeout(() => {
      setProfileDropdownOpen(false);
    }, 300); // 300ms delay before closing
  };

  const handleBlogMouseEnter = () => {
    if (blogCloseTimeout.current) clearTimeout(blogCloseTimeout.current);
    setBlogDropdownOpen(true);
  };

  const handleBlogMouseLeave = () => {
    blogCloseTimeout.current = setTimeout(() => setBlogDropdownOpen(false), 300);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const outsideDesktopProfile = !profileRef.current || !profileRef.current.contains(event.target);
      const outsideMobileProfile  = !mobileProfileRef.current || !mobileProfileRef.current.contains(event.target);
      if (outsideDesktopProfile && outsideMobileProfile) {
        setProfileDropdownOpen(false);
      }
      // Close search only if click is outside both the search icon AND the overlay
      const searchOverlay = document.getElementById('search-dropdown-container');
      if (
        searchRef.current && !searchRef.current.contains(event.target) &&
        (!searchOverlay || !searchOverlay.contains(event.target))
      ) {
        setSearchOpen(false);
      }
      if (blogRef.current && !blogRef.current.contains(event.target)) {
        setBlogDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clean up timeouts on unmount
      if (profileCloseTimeout.current) {
        clearTimeout(profileCloseTimeout.current);
      }
      if (blogCloseTimeout.current) {
        clearTimeout(blogCloseTimeout.current);
      }
      if (searchCloseTimeout.current) {
        clearTimeout(searchCloseTimeout.current);
      }
    };
  }, []);

  // Show mini cart when item is added to cart
  useEffect(() => {
    if (cartNotification) {
      setMiniCartOpen(true);
    }
  }, [cartNotification]);

  return (
    <nav className="navbar">
      <div className="container">
        <div className="nav-wrapper">
          <Link to="/" className="logo">
            <img
              src={logos.UBLong}
              alt="UnsupervisedBias Logo"
              className="logo-img"
              style={{
                transition: 'all 0.3s ease',
                filter: 'drop-shadow(0 0 0px transparent)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.03)';
                e.target.style.filter = 'drop-shadow(0 0 10px rgba(216, 134, 255, 0.8))';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.filter = 'drop-shadow(0 0 0px transparent)';
              }}
            />
          </Link>

          {/* Mobile-only icons — visible in header bar to left of hamburger */}
          <div className="mobile-header-icons">
            <button className="icon-btn" onClick={() => { setSearchOpen(prev => !prev); setIsOpen(false); }} aria-label="Search">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
            <div ref={mobileProfileRef} style={{ position: 'relative' }}>
              <button
                className="icon-btn profile-btn"
                onClick={() => { setProfileDropdownOpen(prev => !prev); setIsOpen(false); }}
                aria-label="Profile"
              >
                {isAuthenticated && user?.profile_image ? (
                  <img src={user.profile_image} alt="Profile" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                )}
              </button>
              {profileDropdownOpen && (
                <div className="dropdown-menu" style={{ position: 'fixed', top: '60px', right: '8px', zIndex: 9999, minWidth: '200px', width: 'auto' }}>
                  {isAuthenticated ? (
                    <>
                      <Link to="/profile" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                        My Account
                      </Link>
                      {/* My Blogs — hidden until blog is ready
                      <Link to="/my-blogs" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                        My Blogs
                      </Link>
                      */}
                      {/* My Services — hidden until services are ready
                      <Link to="/my-services" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                        My Services
                      </Link>
                      */}
                      <Link to="/trading" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
                        Invest
                      </Link>
                      {user?.is_staff && (
                        <>
                          <div className="dropdown-divider"></div>
                          <Link to="/admin/blog-review" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                            Blog Review
                          </Link>
                          <Link to="/admin/database" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path></svg>
                            Database Inspector
                          </Link>
                          <Link to="/admin/jobs" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
                            Jobs &amp; Containers
                          </Link>
                          <Link to="/admin/analytics" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                            Analytics
                          </Link>
                          <Link to="/admin/architecture" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path><path d="M4.93 4.93a10 10 0 0 0 0 14.14"></path></svg>
                            Architecture
                          </Link>
                          <Link to="/admin/dmarc" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                            DMARC Reports
                          </Link>
                        </>
                      )}
                      <div className="dropdown-divider"></div>
                      <button onClick={() => { handleLogout(); setProfileDropdownOpen(false); }} className="dropdown-item dropdown-logout">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/signin" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>Sign In</Link>
                      <Link to="/create-account" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>Create Account</Link>
                    </>
                  )}
                </div>
              )}
            </div>
{/* Cart temporarily hidden
            <Link to="/cart" className="icon-btn cart-icon" aria-label="Cart" onClick={() => setIsOpen(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {cartItems && cartItems.length > 0 && (
                <span className="cart-badge">{cartItems.length}</span>
              )}
            </Link>
*/}
          </div>

          <button className={`hamburger ${isOpen ? 'active' : ''}`} onClick={toggleMenu}>
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className={`nav-menu ${isOpen ? 'active' : ''}`}>
            <Link to="/" onClick={() => setIsOpen(false)}>Home</Link>
            <Link to="/about" onClick={() => setIsOpen(false)}>About</Link>
            {/* <Link to="/services" onClick={() => setIsOpen(false)}>Services</Link> */}
            <Link to="/contact" onClick={() => setIsOpen(false)}>Contact</Link>
            <Link to="/faq" onClick={() => setIsOpen(false)}>FAQ</Link>

            {/* Blog Dropdown - hidden until blog is ready
            <div
              className="nav-link-dropdown"
              ref={blogRef}
              onMouseEnter={handleBlogMouseEnter}
              onMouseLeave={handleBlogMouseLeave}
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <Link
                to="/blog"
              >
                Blog

              </Link>

              {blogDropdownOpen && (
                <div className="dropdown-menu" style={{ minWidth: '180px' }}>
                  <Link
                    to="/blog"
                    className="dropdown-item"
                    onClick={() => {
                      setBlogDropdownOpen(false);
                      setIsOpen(false);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    All Blogs
                  </Link>
                  <Link
                    to="/my-blogs"
                    className="dropdown-item"
                    onClick={() => {
                      setBlogDropdownOpen(false);
                      setIsOpen(false);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    My Blogs
                  </Link>
                </div>
              )}
            </div>
            */}

            {/* Desktop Icons */}
            <div className="nav-icons">
              {/* Search Icon */}
              <div
                className="icon-container"
                ref={searchRef}
              >
                <button
                  className="icon-btn"
                  aria-label="Search"
                  onClick={() => setSearchOpen(prev => !prev)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                </button>
              </div>

              {/* Profile/Account Icon with Dropdown */}
              <div
                className="icon-container profile-dropdown"
                ref={profileRef}
                onMouseEnter={handleProfileMouseEnter}
                onMouseLeave={handleProfileMouseLeave}
              >
                <button
                  className="icon-btn profile-btn"
                  aria-label="Account"
                  onClick={() => {
                    // If authenticated, go to My Account page
                    // If not authenticated, go to Sign In page
                    navigate(isAuthenticated ? '/profile' : '/signin');
                  }}
                >
                  {isAuthenticated && user?.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt="Profile"
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  )}
                </button>

                {profileDropdownOpen && (
                  <div className="dropdown-menu">
                    {isAuthenticated ? (
                      <>
                        <Link to="/profile" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                          My Account
                        </Link>
                        {/* My Blogs — hidden until blog is ready
                        <Link to="/my-blogs" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                          My Blogs
                        </Link>
                        */}
                        {/* My Services — hidden until services are ready
                        <Link to="/my-services" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                          My Services
                        </Link>
                        */}

                        <Link to="/trading" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                            <polyline points="16 7 22 7 22 13"></polyline>
                          </svg>
                          Invest
                        </Link>

                        {/* Admin Links - Only for staff users */}
                        {user?.is_staff && (
                          <>
                            <div className="dropdown-divider"></div>
                            <Link to="/admin/blog-review" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                              </svg>
                              Blog Review
                            </Link>
                            <Link to="/admin/database" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
                                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
                                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
                              </svg>
                              Database Inspector
                            </Link>
                            <Link to="/admin/jobs" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                              </svg>
                              Jobs &amp; Containers
                            </Link>
                            <Link to="/admin/analytics" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="20" x2="18" y2="10"></line>
                                <line x1="12" y1="20" x2="12" y2="4"></line>
                                <line x1="6" y1="20" x2="6" y2="14"></line>
                              </svg>
                              Analytics
                            </Link>
                            <Link to="/admin/architecture" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="3"></circle>
                                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                                <path d="M4.93 4.93a10 10 0 0 0 0 14.14"></path>
                              </svg>
                              Architecture
                            </Link>
                            <Link to="/admin/dmarc" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                <polyline points="22,6 12,13 2,6"></polyline>
                              </svg>
                              DMARC Reports
                            </Link>
                          </>
                        )}

                        <div className="dropdown-divider"></div>
                        <button onClick={handleLogout} className="dropdown-item dropdown-logout">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                            <polyline points="16 17 21 12 16 7"></polyline>
                            <line x1="21" y1="12" x2="9" y2="12"></line>
                          </svg>
                          Sign Out
                        </button>
                      </>
                    ) : (
                      <>
                        <Link to="/signin" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                            <polyline points="10 17 15 12 10 7"></polyline>
                            <line x1="15" y1="12" x2="3" y2="12"></line>
                          </svg>
                          Sign In
                        </Link>
                        <Link to="/create-account" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="8.5" cy="7" r="4"></circle>
                            <line x1="20" y1="8" x2="20" y2="14"></line>
                            <line x1="23" y1="11" x2="17" y2="11"></line>
                          </svg>
                          Create Account
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Cart Icon - temporarily hidden
              <Link to="/cart" className="icon-btn cart-icon" aria-label="Cart">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                {cartItems && cartItems.length > 0 && (
                  <span className="cart-badge">{cartItems.length}</span>
                )}
              </Link>
              */}
            </div>
          </div>
        </div>
      </div>
      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <MiniCart isOpen={miniCartOpen} onClose={handleCloseMiniCart} />
    </nav>
  );
};

export default Navigation;
